/**
 * DocumentViewerDialog
 *
 * A modal dialog that renders an employee document inline in the browser,
 * supporting both image files (JPEG, PNG, WEBP) and PDF documents.
 *
 * When opened the component fetches the document blob from R2 via the API,
 * creates a temporary `blob:` object URL, and renders it either as:
 *   - An `<img>` element for MIME types starting with `image/`
 *   - An `<iframe>` element for `application/pdf`
 *
 * The object URL is revoked on dialog close to prevent memory leaks.
 *
 * @module employees/DocumentViewerDialog
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchEmployeeDocumentBlob } from '../dataProvider';
import type { DocType } from './EmployeeDocumentsSection';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DocumentViewerDialogProps {
    /**
     * Whether the dialog is open.
     */
    open: boolean;

    /**
     * Called when the user requests to close the dialog (close button or
     * backdrop click).
     */
    onClose: () => void;

    /**
     * The numeric `id` of the `employee_accounting` row.
     */
    accountingId: number | string;

    /**
     * The document slot being viewed.
     */
    docType: DocType;

    /**
     * Human-readable label for the document (used as the dialog title).
     */
    label: string;
}

// ---------------------------------------------------------------------------
// DocumentViewerDialog
// ---------------------------------------------------------------------------

/**
 * Modal viewer for employee documents.
 *
 * Fetches the document blob when the dialog first opens, renders images with
 * an `<img>` tag and PDFs inside an `<iframe>`. Shows a spinner while loading
 * and an error message if the fetch fails.
 *
 * @param props.open          - Whether the dialog is visible.
 * @param props.onClose       - Close callback.
 * @param props.accountingId  - ID of the employee_accounting row.
 * @param props.docType       - Document slot identifier.
 * @param props.label         - Human-readable name used as the dialog title.
 */
const DocumentViewerDialog: React.FC<DocumentViewerDialogProps> = ({
    open,
    onClose,
    accountingId,
    docType,
    label,
}) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [mimeType, setMimeType] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Track the current blob URL so we can revoke it when the dialog closes
    // or when a new document is loaded.
    const blobUrlRef = useRef<string | null>(null);

    // Revoke any existing blob URL to free memory.
    const revokeCurrent = () => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
    };

    // Fetch document when dialog opens.
    useEffect(() => {
        if (!open) return;

        let cancelled = false;
        setIsLoading(true);
        setError(null);
        setBlobUrl(null);

        fetchEmployeeDocumentBlob(accountingId, docType)
            .then(({ url, mimeType: mime }) => {
                if (cancelled) {
                    URL.revokeObjectURL(url);
                    return;
                }
                revokeCurrent();
                blobUrlRef.current = url;
                setBlobUrl(url);
                setMimeType(mime);
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load document');
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, accountingId, docType]);

    // Revoke blob URL when the dialog closes.
    const handleClose = () => {
        revokeCurrent();
        setBlobUrl(null);
        setMimeType('');
        setError(null);
        onClose();
    };

    // Clean up on unmount.
    useEffect(() => {
        return () => {
            revokeCurrent();
        };
    }, []);

    // Determine rendering strategy based on MIME type.
    const isImage = mimeType.startsWith('image/');
    const isPdf = mimeType === 'application/pdf';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            data-testid="document-viewer-dialog"
            PaperProps={{ sx: { minHeight: '70vh' } }}
        >
            <DialogTitle
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
                <Typography variant="h6" component="span">
                    {label}
                </Typography>
                <IconButton
                    aria-label="Close document viewer"
                    onClick={handleClose}
                    size="small"
                    data-testid="document-viewer-close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    overflow: 'hidden',
                }}
            >
                {/* Loading state */}
                {isLoading && (
                    <CircularProgress data-testid="document-viewer-loading" />
                )}

                {/* Error state */}
                {!isLoading && error && (
                    <Typography
                        color="error"
                        data-testid="document-viewer-error"
                    >
                        {error}
                    </Typography>
                )}

                {/* Image viewer */}
                {!isLoading && !error && blobUrl && isImage && (
                    <Box
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '75vh',
                            overflow: 'auto',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <img
                            src={blobUrl}
                            alt={label}
                            style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
                            data-testid="document-viewer-image"
                        />
                    </Box>
                )}

                {/* PDF viewer */}
                {!isLoading && !error && blobUrl && isPdf && (
                    <iframe
                        src={blobUrl}
                        title={label}
                        style={{ width: '100%', height: '70vh', border: 'none' }}
                        data-testid="document-viewer-pdf"
                    />
                )}

                {/* Unsupported type fallback */}
                {!isLoading && !error && blobUrl && !isImage && !isPdf && (
                    <Typography
                        color="text.secondary"
                        data-testid="document-viewer-unsupported"
                    >
                        Preview is not available for this file type ({mimeType}).
                        Please use the download button instead.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DocumentViewerDialog;
